(ns app.front.view.application-wizard.attachments
  (:require
    ["@carbon/icons-react/es/add/16" :default Add16]
    ["@carbon/icons-react/es/document/16" :default Document16]
    [app.domain.attachment.file-formats :as file-formats]
    [app.domain.attachment.model :as attachment-model]
    [app.front.entities :as entities]
    [app.front.file.upload :as upload]
    [app.front.process :as process]
    [app.front.process.save-tracking :as save-tracking]
    [app.front.requirement.components :as req-components]
    [app.front.requirement.events :as req-events]
    [app.front.requirement.subs :as req-subs]
    [app.front.ui :as ui]
    [app.front.util :refer [for-all]]
    [app.front.view.attachment.list :as attachment-list]
    [app.front.view.attachment.subs :as attachment-subs]
    [app.front.view.workspace.subs :as ws-subs]
    [core.front.carbon :as cc]
    [core.front.event :refer [<sub >evt]]
    [core.front.icons :as i]
    [potpuri.core :as p]
    [re-frame.core-instrumented :as rf]
    [reagent.core :as r]))


; NOTE: This being under a /:workspace-id route, the ::ws-get/get-for-application-view event has been dispatched and
; many entities related to the workspace have been loaded into the frontend entity db from the :workspace/get command.
; This includes the workspace's application, files, requirements, attachments, etc. See the :find-related parameter
; passed to :workspace/get in app.front.view.workspace.get for the full list.


;; Files

(rf/reg-sub ::workspace-files
  :<- [::entities/context]
  :<- [::ws-subs/workspace]
  (fn [[ctx workspace] _]
    (when workspace
      (entities/find-entities ctx {:entity/type   :entity.type/file
                                   :entity/parent (:db/id workspace)
                                   :file/category :file.category/attachment-candidate}))))

(rf/reg-sub ::hanging-files?
  :<- [::workspace-files]
  :-> #(boolean (seq %)))

(rf/reg-event-fx ::tracked-command-finished
  (fn [_ [_ operation-id status _body]]
    {:dispatch [(if (= 200 status)
                  ::save-tracking/save-succeeded
                  ::save-tracking/save-failed)
                operation-id]}))

(rf/reg-event-fx ::tracked-command-error
  ;; Overriding :on-error replaces comm's default error handling, so keep its console logging.
  (fn [_ [_ operation-id status]]
    (js/console.error "::tracked-command-error" status)
    {:dispatch-n [[::save-tracking/save-failed operation-id]
                  [:notifications/add
                   :toast
                   :error
                   :error/connection-error-title
                   :error/connection-error-description]]}))

(rf/reg-event-fx ::add-workspace-file
  (fn [{:keys [db]} [_ {:keys [file-id filename file-mime-type md5-base64]} attachment-type-id]]
    (let [workspace-id (-> db :route/match :parameters :path :workspace-id)
          operation-id (random-uuid)]
      {:fx [[:dispatch [::save-tracking/save-started operation-id]]
            [:comm/command {:command          :file/add-workspace-file
                            :data             {:workspace-id      workspace-id
                                               :file-id           file-id
                                               :file/filename     filename
                                               :file/content-type file-mime-type
                                               :file/category     :file.category/attachment-candidate
                                               :file/md5          md5-base64}
                            :on-success       [::add-workspace-file-success attachment-type-id]
                            :on-error         [::tracked-command-error operation-id]
                            :finally-dispatch [::tracked-command-finished operation-id]}]]})))

(rf/reg-event-fx ::add-workspace-file-success
  (fn [{:keys [db]} [_ attachment-type-id status response]]
    (let [file-id (ffirst response)]
      (merge {:dispatch [::entities/merge-entity-db status response]}
             (when attachment-type-id
               {:db (assoc-in db [::file-metadata file-id :attachment/types] [attachment-type-id])})))))

(rf/reg-event-fx ::delete-workspace-file
  (fn [_ [_ file-id operation-id]]
    (let [operation-id (or operation-id (random-uuid))]
      {:fx [[:dispatch [::save-tracking/save-started operation-id]]
            [:comm/command {:command          :file/delete-workspace-file
                            :data             {:file-id file-id}
                            :on-success       [::delete-workspace-file-success file-id]
                            :on-error         [::tracked-command-error operation-id]
                            :finally-dispatch [::tracked-command-finished operation-id]}]]})))

(rf/reg-event-fx ::delete-workspace-file-success
  (fn [{:keys [db]} [_ file-id _ _]]
    {:db       (update db ::file-metadata dissoc file-id)
     :dispatch [::entities/remove-entity file-id]}))

(rf/reg-event-fx ::cancel-attachments
  (fn [_ [_ files]]
    {:dispatch-n (->> files
                      (filter #(= :file.category/attachment-candidate (:file/category %)))
                      (mapv #(vector ::delete-workspace-file (:db/id %))))}))


;; File metadata: temporary state for the user's selection of the metadata required to turn files into attachments.

(rf/reg-sub ::file-metadata :-> ::file-metadata)

(defn- file-ai-attachment-suggestion [file]
  (-> file :file/metadata :wizard-ai/attachment-suggestion))

(defn- file-attachment-metadata [metadata file]
  (merge (file-ai-attachment-suggestion file)
         (get metadata (:db/id file))))

(rf/reg-sub ::file-ai-attachment-suggestion
  :<- [::entities/context]
  (fn [ctx [_ file-id]]
    (file-ai-attachment-suggestion (entities/find-entity ctx file-id))))

(rf/reg-sub ::file-attachment-types
  (fn [[_ file-id] _]
    [(rf/subscribe [::file-metadata])
     (rf/subscribe [::file-ai-attachment-suggestion file-id])])
  (fn [[metadata suggestion] [_ file-id]]
    (or (get-in metadata [file-id :attachment/types])
        (:attachment/types suggestion))))

(rf/reg-sub ::file-drawing-number
  (fn [[_ file-id] _]
    [(rf/subscribe [::file-metadata])
     (rf/subscribe [::file-ai-attachment-suggestion file-id])])
  (fn [[metadata suggestion] [_ file-id]]
    (or (get-in metadata [file-id :attachment/drawing-number])
        (:attachment/drawing-number suggestion))))

(rf/reg-sub ::file-description
  (fn [[_ file-id] _]
    [(rf/subscribe [::file-metadata])
     (rf/subscribe [::file-ai-attachment-suggestion file-id])])
  (fn [[metadata suggestion] [_ file-id]]
    (or (get-in metadata [file-id :attachment/contents])
        (:attachment/contents suggestion))))

(rf/reg-event-db ::set-file-attachment-types
  (fn [db [_ file-id types]]
    (assoc-in db [::file-metadata file-id :attachment/types] types)))

(rf/reg-event-db ::set-file-drawing-number
  (fn [db [_ file-id value]]
    (assoc-in db [::file-metadata file-id :attachment/drawing-number] value)))

(rf/reg-event-db ::set-file-description
  (fn [db [_ file-id value]]
    (assoc-in db [::file-metadata file-id :attachment/contents] value)))



(rf/reg-sub ::attachment-type-options
  :<- [:app.front.view.application/attachment-types-with-i18n]
  (fn [attachment-types _]
    (->> attachment-types
         (mapv (fn [[label id]] {:id id :label label})))))

(rf/reg-sub ::attachment-type-ids-from-attachments
  (fn [[_ app-id] _]
    (rf/subscribe [::attachments app-id]))
  (fn [attachments _]
    (->> attachments
         (mapcat :attachment/types)
         (map :db/id)
         set)))

(rf/reg-sub ::requirement-satisfied-by-attachments?
  (fn [[_ app-id _] _]
    (rf/subscribe [::attachment-type-ids-from-attachments app-id]))
  (fn [attached-type-ids [_ _ requirement-type-id]]
    (contains? attached-type-ids requirement-type-id)))

(rf/reg-sub ::all-files-have-types?
  :<- [::file-metadata]
  :<- [::workspace-files]
  (fn [[metadata files] _]
    (every? (fn [file]
              (seq (:attachment/types (file-attachment-metadata metadata file))))
            files)))

;; Files -> Attachments when user clicks done.

(rf/reg-sub ::creating-attachment-file-ids
  :-> ::creating-attachment-file-ids)

(rf/reg-sub ::creating-attachments?
  :<- [::creating-attachment-file-ids]
  (fn [file-ids _]
    (boolean (seq file-ids))))

(rf/reg-event-fx ::attachment-create-done
  (fn [{:keys [db]} [_ operation-id file-id status _body]]
    (p/assoc-if {:db (update db ::creating-attachment-file-ids disj file-id)}
                :dispatch (when (not= 200 status)
                            [::save-tracking/save-failed operation-id]))))

(rf/reg-event-fx ::confirm-attachments
  (fn [{:keys [db]} [_ {:keys [app-id files]}]]
    (when-not (seq (::creating-attachment-file-ids db))
      (let [metadata (::file-metadata db)
            file-ids (set (map :db/id files))]
        {:db (assoc db ::creating-attachment-file-ids file-ids)
         :fx (mapv (fn [file]
                     (let [file-id      (:db/id file)
                           meta         (file-attachment-metadata metadata file)
                           operation-id (random-uuid)
                           signal       [:process.signal/attachment-create-with-version
                                         (cond-> {:attachment/types    (:attachment/types meta)
                                                  :file-id             ""
                                                  :filename            (:file/filename file)
                                                  :content-type        (:file/content-type file)
                                                  :from-workspace-file file-id}
                                           (:attachment/contents meta)
                                           (assoc :attachment/contents (:attachment/contents meta))
                                           (:attachment/drawing-number meta)
                                           (assoc :attachment/drawing-number (:attachment/drawing-number meta)))]]
                       [:dispatch [::process/signal app-id signal
                                   {:on-success   [::delete-workspace-file file-id operation-id]
                                    :on-done      [::attachment-create-done operation-id file-id]
                                    :on-error     [::attachment-create-done operation-id file-id]
                                    :track-save?  true
                                    :operation-id operation-id
                                    :chained?     true}]]))
                   files)}))))

;; Attachments

(rf/reg-sub ::attachments
  (fn [[_ app-id] _]
    (rf/subscribe [::attachment-subs/attachments-for-parent app-id]))
  :-> vec)

(rf/reg-sub ::has-attachments?
  (fn [[_ app-id] _]
    (rf/subscribe [::attachments app-id]))
  (fn [attachments _]
    (boolean (seq attachments))))

(rf/reg-sub ::step-completed?
  :<- [::ws-subs/application]
  :<- [::entities/context]
  (fn [[application ctx] _]
    (when-let [app-id (:db/id application)]
      (let [attachments       (entities/find-entities ctx {:entity/type   :entity.type/attachment
                                                           :entity/parent app-id})
            requirements      (entities/find-entities ctx {:entity/type      :entity.type/requirement
                                                           :entity/parent    app-id
                                                           :requirement/kind :requirement.kind/attachment})
            attached-type-ids (->> attachments
                                   (mapcat :attachment/types)
                                   (map :db/id)
                                   set)]
        (every? (fn [req]
                  (contains? attached-type-ids
                             (-> req :requirement/attachment-type :db/id)))
                requirements)))))


;; Views

(defn- format-size [size]
  (when size
    (if (> size 1048576)
      (str (.toFixed (/ size 1048576) 2) " MB")
      (str (.toFixed (/ size 1024) 1) " KB"))))

(def input-class "rounded border border-solid border-gray-400")

(defn- attachment-type-select [{:keys [tr file-id]}]
  (let [options  (or (<sub [::attachment-type-options]) [])
        selected (<sub [::file-attachment-types file-id])]
    [:div.multi-select
     (cc/multi-select
       {:id                     (str "file-type-" file-id)
        :class-name             (str "attachment-type-multi-select " input-class)
        :light                  true
        :title-text             (str (tr [:upload/document-type]) "*")
        :label                  (if (seq selected)
                                  (tr [:application-wizard/attachments-items-selected] [(count selected)])
                                  (tr [:upload/type]))
        :items                  options
        :initial-selected-items (if (seq selected)
                                  (filterv #(contains? (set selected) (:id %)) options)
                                  [])
        :on-change              (fn [^js e]
                                  (let [values (->> (.-selectedItems e)
                                                    js->clj
                                                    (mapv #(get % "id")))]
                                    (>evt [::set-file-attachment-types file-id values])))})]))

(defn- file-card [{:keys [tr]} {:file/keys [filename size] :as file}]
  (let [file-id (:db/id file)]
    [:div.border.border-solid.border-gray-300.p-4.rounded.bg-white
     ;; Header: icon + filename + size + close button
     [:div.flex.justify-between.items-center.mb-4
      [:div.flex.items-center.gap-2
       [:> Document16 {:className "text-gray-500"}]
       [:div
        [:span.text-sm.font-medium filename]
        (when size
          [:div.text-xs.text-gray-500 (format-size size)])]]
      [:button.bg-white.border-none
       {:on-click   #(>evt [::delete-workspace-file file-id])
        :aria-label (tr [:upload/remove-attachment])}
       (cc/icon {:icon i/remove :description (tr [:upload/remove-attachment])})]]
     ;; Document type + Drawing number
     [:div.flex.flex-col.sm:flex-row.gap-4.mb-4
      [:div {:class "flex-[3] font-sm"}
       [attachment-type-select {:tr tr :file-id file-id}]]
      [:div {:class "flex-[2]"}
       [ui/text-input {:id          (str "drawing-number-" file-id)
                       :label-text  (tr [:upload/drawing-number])
                       :input-class input-class
                       :light       true
                       :value       (or (<sub [::file-drawing-number file-id]) "")
                       :on-change   #(>evt [::set-file-drawing-number file-id (-> % .-target .-value)])}]]]
     ;; Description
     [ui/text-input {:id          (str "description-" file-id)
                     :light       true
                     :input-class input-class
                     :label-text  (tr [:upload/description])
                     :value       (or (<sub [::file-description file-id]) "")
                     :on-change   #(>evt [::set-file-description file-id (-> % .-target .-value)])}]]))

(def ^:private requirement-button-class
  (str "w-full h-12 flex items-center justify-between relative rounded-md border border-solid"
       " border-gray-300 p-4 bg-transparent text-left"))

(defn- satisfied-requirement-button [{:keys [loading-config? label]}]
  [:div.mx-4
   [:div {:class requirement-button-class}
    [:div.absolute.left-0.top-0.bottom-0.w-2.rounded-l-md.bg-green-600]
    [:span.font-bold.tracking-wide.text-sm
     (if loading-config? (cc/skeleton-text) label)]]])

(defn- unsatisfied-requirement-button [{:keys [tr loading-config? label attachment-type-id]}]
  [upload/file-picker-button
   {:id           (str "wizard-required-attachment-" attachment-type-id)
    :tr           tr
    :class        "mx-4"
    :multiple     true
    :accept-types file-formats/accept-value-for-input
    :on-select-fn (partial upload/process-file-upload tr
                           (fn [file] (>evt [::add-workspace-file file attachment-type-id])))
    :button-opts  {:kind       :ghost
                   :type       :button
                   :disabled   loading-config?
                   :class-name (str requirement-button-class
                                    " !max-w-none !normal-case !text-inherit"
                                    " hover:!bg-gray-50 hover:!border-gray-300"
                                    " focus:!bg-transparent active:!bg-transparent")}}
   ^{:key (str "wizard-required-attachment-" attachment-type-id)}
   [:<>
    [:div.absolute.left-0.top-0.bottom-0.w-2.rounded-l-md.bg-orange-400]
    [:span.font-bold.tracking-wide.text-sm
     (if loading-config? (cc/skeleton-text) label)]
    [:> Add16]]])

(defn removable-requirement-button
  [{:keys [loading-config? label satisfied? remove-loading? remove-label on-remove]}]
  [:div.mx-4
   [:button
    {:type       "button"
     :class      (str requirement-button-class
                      " cursor-pointer hover:bg-gray-50 focus:bg-gray-50"
                      " disabled:cursor-not-allowed disabled:opacity-50")
     :aria-label remove-label
     :data-cy    "wizard-remove-attachment-requirement"
     :disabled   (or loading-config? remove-loading?)
     :on-click   on-remove}
    [:div.absolute.left-0.top-0.bottom-0.w-2.rounded-l-md
     {:class (if satisfied? "bg-green-600" "bg-orange-400")}]
    [:span.font-bold.tracking-wide.text-sm
     (if loading-config? (cc/skeleton-text) label)]
    (cc/icon {:icon        i/remove
              :height      "24px"
              :description ""
              :aria-hidden true})]])

(defn- requirement-button [{:keys [satisfied?] :as opts}]
  (cond
    (:can-remove-requirements? opts)
    [removable-requirement-button opts]

    satisfied?
    [satisfied-requirement-button opts]

    :else
    [unsatisfied-requirement-button opts]))

(defn- file-upload-area [_tr]
  [upload/upload-area "wizard-attachments-upload"
   {:attach-file-v2-fn #(>evt [::add-workspace-file %])
    :accept-types      file-formats/accept-value-for-input}])

(defn- required-attachments-buttons [tr application]
  (let [app-id                      (:db/id application)
        requirements                (<sub [::req-subs/all-attachment-requirements app-id])
        loading-config?             (<sub [::ws-subs/loading-config?])
        add-loading?                (<sub [::req-subs/add-loading?])
        remove-loading?             (<sub [::req-subs/remove-loading?])
        attachment-types            (<sub [::req-subs/attachment-types requirements])
        can-add-requirements?       (<sub [::req-subs/can-add-requirements? app-id])
        can-remove-requirements?    (<sub [::process/signal-permitted?
                                           app-id
                                           [:process.signal/remove-attachment-requirement]])
        add-requirement-popup-open? (<sub [::req-components/add-requirement-popup-open?])]
    (when (or (seq requirements) can-add-requirements?)
      [:div
       [:h5 (tr [:application-wizard/required-attachments])]
       (when (seq requirements)
         (into [:ol.flex.flex-col.my-6.gap-3.lg:flex-row.lg:flex-wrap]
               (interpose
                 [:div.hidden.lg:block.w-px.h-12.bg-gray-300]
                 (for [{:requirement/keys [title attachment-type]} requirements]
                   (let [attachment-type-id (:db/id attachment-type)
                         satisfied?         (<sub [::requirement-satisfied-by-attachments? app-id attachment-type-id])]
                     [:li.list-decimal.ml-9.lg:min-w-80
                      [requirement-button {:tr                       tr
                                           :loading-config?          loading-config?
                                           :label                    title
                                           :satisfied?               satisfied?
                                           :attachment-type-id       attachment-type-id
                                           :can-remove-requirements? can-remove-requirements?
                                           :remove-loading?          remove-loading?
                                           :remove-label             (tr [:attachments/remove-required-attachment]
                                                                         [title])
                                           :on-remove                #(>evt [::req-events/remove-attachment-requirement
                                                                             app-id
                                                                             attachment-type])}]])))))
       (when can-add-requirements?
         [:div.w-full.lg:w-80
          {:class (if (seq requirements) "mb-6" "my-6")}
          [req-components/add-attachment-requirement-controls
           {:popup-open?         add-requirement-popup-open?
            :add-loading?        add-loading?
            :tr                  tr
            :attachment-types    attachment-types
            :target-id           app-id
            :placeholder-button? true}]])])))

(defn- attachment-row [_tr _attachment _lang _app-id]
  (r/with-let [*expanded? (r/atom false)]
    (fn [tr attachment lang app-id]
      (let [filename   (some-> attachment :attachment/versions first
                               :attachment.version/files first
                               :attachment.version.file/filename)
            type-label (attachment-model/attachment-title lang attachment)
            expanded?  @*expanded?]
        [:div.rounded-md.relative.bg-ui-light-gray
         [:div.absolute.left-0.top-0.bottom-0.w-2.rounded-l-md.bg-green-600]
         ;; Summary row
         [:div.grid.items-center.px-6.py-5.cursor-pointer.select-none
          {:class    "grid-cols-[40px_1fr_1fr]"
           :on-click #(swap! *expanded? not)}
          [:div
           (cc/icon {:icon        (if expanded? i/chevron-up i/chevron-down)
                     :description (tr [:application-wizard/attachments-toggle-details])})]
          [:span type-label]
          [:span filename]]
         ;; Expandable body
         (when expanded?
           [:div.px-6.py-5.bg-white.border-b.border-r.border-solid.border-gray-400.rounded-b-md
            [attachment-list/expanded-attachment-row {:id                (:db/id attachment)
                                                      :tr                tr
                                                      :process-target-id app-id
                                                      :property?         false
                                                      :bond?             false}]])]))))

(defn- attachment-list [tr application]
  (let [attachments (<sub [::attachments (:db/id application)])
        lang        (<sub [:language])]
    [:div.flex.flex-col.gap-2
     ;; Header
     [:div.grid.items-center.rounded-md.px-6.py-4.font-bold.bg-gray-200
      {:class "grid-cols-[40px_1fr_1fr]"}
      [:div]
      [:div (tr [:upload/document-type])]
      [:div (tr [:upload/filename])]]
     ;; Rows
     (for-all [att attachments]
       ^{:key (:db/id att)}
       [attachment-row tr att lang (:db/id application)])
     [attachment-list/modals {:tr                    tr
                              :process-target-id     (:db/id application)
                              :property?             false
                              :bond?                 false
                              :render-preview-modal? true}]]))

(defn- file-cards [tr application]
  (let [all-files-have-types? (<sub [::all-files-have-types?])
        files                 (<sub [::workspace-files])
        creating-attachments? (<sub [::creating-attachments?])]
    (when (seq files)
      [:div.mb-6.bg-ui-blue-10.p-4.rounded-md.border-2.border-ui-blue-100.border-solid
       [:div.grid.grid-cols-1.lg:grid-cols-2.xl:grid-cols-3.gap-4
        (for [file files]
          ^{:key (:db/id file)}
          [file-card {:tr tr} file])]

       ;; Cancel and save attachments buttons
       [:div.mt-6.flex.justify-end
        (if creating-attachments?
          [:div.w-fit
           (cc/inline-loading {:description (tr [:application-wizard/attachments-creating])})]
          [:div.flex.gap-2.flex-wrap
           [ui/button {:kind     :secondary
                       :on-click #(>evt [::cancel-attachments files])}
            (tr [:cancel])]
           [ui/button {:kind     :primary
                       :disabled (not all-files-have-types?)
                       :on-click #(>evt [::confirm-attachments {:app-id (:db/id application)
                                                                :files  files}])}
            (tr [:application-wizard/attachments-create])]])]])))


(defn view [tr]
  (let [application      (<sub [::ws-subs/application])
        app-id           (:db/id application)
        has-attachments? (when app-id (<sub [::has-attachments? app-id]))]
    (when application
      [:div
       [required-attachments-buttons tr application]
       [file-upload-area tr]
       [file-cards tr application]
       (when has-attachments?
         [attachment-list tr application])])))
